import { useEffect, useState } from "react";
import { ArrowRight, Compass, RotateCcw, Sparkles } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { PageBand } from "@/components/kit/PageBand";
import { api, routes } from "@/lib/ppcp";
import { useProgress } from "@/lib/progress";
import { KIT, PAGES } from "@/constants/testIds";

const Stat = ({ value, label }) => (
  <div className="min-w-[130px]">
    <p className="font-display text-[2.5rem] font-semibold leading-none text-brand-teal">{value}</p>
    <p className="mt-2 text-[13px] font-bold uppercase tracking-[0.16em] text-brand-teal/50">{label}</p>
  </div>
);

export default function Completion() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { summary, reset } = useProgress();
  const [pack, setPack] = useState(null);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    api.get(`/programs/${slug}`).then((res) => setPack(res.data)).catch(() => {});
  }, [slug]);

  const program = pack?.program;
  const total = summary?.total_days ?? program?.duration_days ?? 30;
  const done = summary?.days_complete ?? 0;
  // Everything here reflects what the reader actually cooked, never the size of the plan.
  const weeksCooked = Math.floor(done / 7);
  const mealsPrepared = program?.slots?.length ? done * program.slots.length : null;

  const restart = async () => {
    setRestarting(true);
    try {
      await reset();
      navigate(routes.day(slug, 1));
    } finally {
      setRestarting(false);
    }
  };

  return (
    <AppShell
      title="You finished the plan"
      slug={slug}
      size="reading"
      crumbs={[
        { label: "My Plans", to: routes.library },
        { label: program?.title ?? "Program", to: routes.program(slug) },
        { label: "Finished" },
      ]}
    >
      <div data-testid={PAGES.completion} className="contents">
        <PageBand
          eyebrow="The last page"
          title="Congratulations — you did it"
          accent="Congratulations"
          intro="Thirty days of home-cooked, balanced meals. You didn't follow a crash diet or count a single calorie — you simply cooked, ate mindfully and kept going."
        />

        <section className="ps-card ps-card-pad space-y-7">
          <div className="flex flex-wrap items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-brand-green/10">
              <Sparkles className="h-6 w-6 text-brand-green" />
            </span>
            <h2 className="font-display text-2xl font-semibold text-brand-teal sm:text-3xl">
              Your 30 days, in numbers
            </h2>
          </div>

          <div className="flex flex-wrap gap-x-12 gap-y-7">
            <Stat value={`${done}/${total}`} label="Days completed" />
            <Stat value={`${weeksCooked}`} label="Weeks of cooking" />
            {mealsPrepared !== null && <Stat value={`${mealsPrepared}`} label="Meals prepared" />}
            <Stat value={`${summary?.percent ?? 0}%`} label="Of the plan" />
          </div>

          <div className="h-3 w-full overflow-hidden rounded-full bg-brand-teal/[0.08]">
            <div
              className="h-full rounded-full bg-brand-green"
              style={{ width: `${summary?.percent ?? 0}%` }}
            />
          </div>

          <div className="rounded-[20px] border-l-[5px] border-brand-orange bg-brand-orange/[0.06] px-6 py-5">
            <p className="text-[17px] leading-[1.8] text-brand-teal">
              <span className="font-semibold">What now?</span> You don't need a new diet. Pick five
              meals you genuinely enjoyed and keep them in your weekly rotation — that is the whole
              habit, and you already have it.
            </p>
          </div>
        </section>

        {/* Clear next actions, in the order most people want them. */}
        <section className="ps-card ps-card-pad space-y-4">
          <h2 className="font-display text-2xl font-semibold text-brand-teal">Where to next?</h2>

          <button
            data-testid={KIT.completionRestart}
            disabled={restarting}
            onClick={restart}
            className="ps-tap group flex w-full items-center gap-5 rounded-[18px] bg-brand-teal px-6 py-5 text-left transition-colors hover:bg-brand-tealDeep disabled:opacity-60"
          >
            <RotateCcw className="h-6 w-6 shrink-0 text-white" />
            <span className="min-w-0 flex-1">
              <span className="block font-display text-lg font-semibold text-white sm:text-xl">
                {restarting ? "Starting over…" : "Start the plan again from Day 1"}
              </span>
              <span className="mt-0.5 block text-[14px] text-white/70">
                Clears your ticks so you can run the 30 days fresh. The plan is designed to repeat.
              </span>
            </span>
            <ArrowRight className="h-6 w-6 shrink-0 text-white transition-transform duration-300 group-hover:translate-x-1" />
          </button>

          {/* My Plans lives in the persistent bottom navigation, so it is not repeated here. */}
          {/* The archive on Plattershare, so no single plan is presented as the next step. */}
          <a
            data-testid={KIT.completionExplore}
            href="https://www.plattershare.com/meal-plans-and-courses/"
            className="ps-tap group flex items-center gap-5 rounded-[18px] bg-brand-teal px-6 py-5 shadow-card transition-all duration-300 hover:bg-brand-tealDeep hover:shadow-cardHover"
          >
            <Compass className="h-6 w-6 shrink-0 text-brand-mint" />
            <span className="min-w-0 flex-1">
              <span className="block font-display text-lg font-semibold text-white sm:text-xl">
                Explore another meal plan →
              </span>
              <span className="mt-0.5 block text-[14px] text-white/75">
                Explore our other meal plans and find the one that's right for you.
              </span>
            </span>
          </a>
        </section>
      </div>
    </AppShell>
  );
}
